/** Embed safety (§5B). Third-party URLs are never injected as raw HTML — we
 *  accept only known providers, extract the id, and build a known-good iframe
 *  src ourselves. */

export interface ParsedEmbed {
  provider: "youtube" | "vimeo";
  src: string;
  title: string;
}

function youTubeId(u: URL): string | null {
  const host = u.hostname.replace(/^www\./, "");
  if (host === "youtu.be") return u.pathname.slice(1).split("/")[0] || null;
  if (host === "youtube.com" || host === "m.youtube.com") {
    if (u.pathname === "/watch") return u.searchParams.get("v");
    const m = u.pathname.match(/^\/(embed|shorts|v)\/([A-Za-z0-9_-]+)/);
    if (m) return m[2];
  }
  return null;
}

function vimeoId(u: URL): string | null {
  const host = u.hostname.replace(/^www\./, "");
  if (host === "vimeo.com") {
    const m = u.pathname.match(/\/(\d+)/);
    return m ? m[1] : null;
  }
  if (host === "player.vimeo.com") {
    const m = u.pathname.match(/\/video\/(\d+)/);
    return m ? m[1] : null;
  }
  return null;
}

const ID_RE = /^[A-Za-z0-9_-]+$/;

/** Returns a safe iframe src for a supported provider, or null. */
export function parseEmbed(url: string | undefined): ParsedEmbed | null {
  if (!url) return null;
  let u: URL;
  try {
    u = new URL(url.trim());
  } catch {
    return null;
  }
  if (u.protocol !== "https:" && u.protocol !== "http:") return null;

  const yt = youTubeId(u);
  if (yt && ID_RE.test(yt)) {
    return { provider: "youtube", src: `https://www.youtube.com/embed/${yt}`, title: "YouTube video player" };
  }
  const vi = vimeoId(u);
  if (vi && ID_RE.test(vi)) {
    return { provider: "vimeo", src: `https://player.vimeo.com/video/${vi}`, title: "Vimeo video player" };
  }
  return null;
}
